import {
  WidgetChatScreen,
  WidgetRecFlowScreen,
  WidgetReviewFlowScreen,
  WidgetPriceScreen,
  WidgetProductDetailsScreenSettings,
  WidgetReviewCompleteScreenSettings,
  WidgetReviewMyReviewsScreenSettings,
  type WidgetScreen,
  type WidgetSignInScreen,
  WidgetTerpenesScreen,
} from '@/types'

export const DEFAULT_SCREEN_SETTINGS: {
  // Universal steps
  productDetails: WidgetProductDetailsScreenSettings
  signIn: WidgetSignInScreen
  welcome: WidgetScreen
  selectStore: WidgetScreen
  selectMenuType: WidgetScreen
  howItWorks: WidgetScreen
  walletPass: WidgetScreen
  aboutTerpenes: WidgetScreen
  // Recommendations flow steps
  recCategory: WidgetRecFlowScreen
  recSubCategory: WidgetRecFlowScreen
  recEffects: WidgetRecFlowScreen
  recPotency: WidgetRecFlowScreen
  recTerpenes: WidgetTerpenesScreen
  recPrice: WidgetPriceScreen
  recommendations: WidgetScreen
  recSignIn: WidgetSignInScreen
  // Review flow steps
  reviewSignIn: WidgetSignInScreen
  reviewMyReviews: WidgetReviewMyReviewsScreenSettings
  stars: WidgetReviewFlowScreen
  reviewEffects: WidgetReviewFlowScreen
  reviewGoodExperience: WidgetReviewFlowScreen
  reviewBadExperience: WidgetReviewFlowScreen
  reviewPotency: WidgetReviewFlowScreen
  reviewComments: WidgetReviewFlowScreen
  reviewClaimPoints: WidgetSignInScreen
  reviewComplete: WidgetReviewCompleteScreenSettings
  // Chat flow steps
  chat: WidgetChatScreen
} = {
  productDetails: {
    buyAgainButtonText: 'Buy Again',
    findSimilarButtonText: 'Find Similar',
  },
  signIn: {
    title: 'Sign in to your loyalty account',
    infoBoxText:
      'This is solely for the purpose of connecting loyalty programs. We will <b>not</b> send marketing messages to this number without your consent.',
    confirmTitle: 'Confirm Code',
    confirmDescription:
      'We sent a code to the information provided, confirm the code to complete registration',
    confirmButtonText: 'Confirm',
    resendButtonText: 'Resend',
  },
  welcome: {
    title: 'Find your match',
    description: "Let's help you find the right cannabis for you.",
  },
  selectStore: {
    title: 'Select a Store',
  },
  selectMenuType: {
    title: 'Select a Menu',
  },
  recCategory: {
    title: 'What product type are you interested in?',
  },
  recSubCategory: {
    title: 'Which kinds of {category} would you like?',
    description: '(select up to 2)',
    priority: 0,
  },
  recEffects: {
    title: 'How would you like to feel?',
    description: '(select up to 2)',
    edibleTitle: 'Select an effect profile',
    edibleDescription: '(select up to 2)',
    priority: 1,
  },
  recPotency: {
    title: 'How potent would you like it?',
    edibleTitle: 'Dosage per serving',
    priority: 2,
  },
  recTerpenes: {
    title: 'Select up to {maxTerpenes} terpenes',
    description:
      'Terpenes are flavor notes with unique effects, [learn more about terpenes here.]({learnMoreLink})',
    priority: 3,
  },
  recPrice: {
    title: "What's your price range?",
    priority: 4,
  },
  walletPass: {
    title: 'Add your membership card to {platform} Wallet',
    description: 'Track rewards and stay up-to-date with store announcements',
  },
  recommendations: {
    title: 'Here are your top recommendations:',
    description: "Because you're looking for {category} with {effect} effects",
    loadingTitle: 'Creating your recommendations...',
    loadingDescription: '',
  },
  howItWorks: {
    title: 'How does it work?',
    textBlocks: [
      'The AIQ AI Sales Agent is your smart online budtender. We help you find the best product by learning from your reviews and helping you target specific outcomes.',
      'We use advanced data science methods on hundreds and thousands of data points across lab-tested cannabinoid and terpene profiles and verified product reviews to generate your recommendations. Our engine is fueled by lab tests from our partnered brands and retail locations, ensuring you get the highest quality and best lab-tested products available based on the actual chemistry behind each product.',
      'Our bot is designed to improve with each review you submit, growing to align with your unique endocannabinoid system and earning you exclusive discounts from your favorite online dispensaries.',
      'We hope you enjoy.',
    ],
  },
  aboutTerpenes: {
    title: 'Read more about Terpenes',
    description:
      "Terpenes are flavor notes with unique effects, select each one to read about it's composition and flavors.",
  },
  recSignIn: {
    title: 'Sign in to claim your {points} points',
    noPointsTitle: 'Sign in to loyalty',
    infoBoxText:
      'This is solely for the purpose of connecting loyalty programs. We will <b>not</b> send marketing messages to this number without your consent.',
    confirmTitle: 'Confirm Code',
    confirmDescription:
      'We sent a code to the information provided, confirm the code to complete registration',
    confirmButtonText: 'Confirm',
    resendButtonText: 'Resend',
  },
  reviewSignIn: {
    title: 'Reviews',
    description: 'Sign in with your checkout information to write reviews',
    infoBoxText:
      'This is solely for the purpose of connecting loyalty programs. We will <b>not</b> send marketing messages to this number without your consent.',
    confirmTitle: 'Confirm Code',
    confirmDescription:
      'We sent a code to the information provided, confirm the code to complete registration',
    confirmButtonText: 'Confirm',
    resendButtonText: 'Resend',
  },
  reviewMyReviews: {
    pendingReviewsTitle: 'Write a review',
    completedReviewsTitle: 'My reviews',
    pendingReviewsDescription: '',
    completedReviewsDescription: '',
    viewMoreButtonText: 'View all',
  },
  stars: {
    title: 'How many stars?',
    enabled: true,
    required: true,
    priority: 0,
  },
  reviewEffects: {
    title: 'What effects or sensations did you feel?',
    description: '(select all that apply)',
    enabled: true,
    required: true,
    priority: 1,
  },
  reviewGoodExperience: {
    title: 'Which experiences did it pair 👍 well with?',
    description: '(select all that apply)',
    enabled: true,
    required: true,
    priority: 2,
  },
  reviewBadExperience: {
    title: 'Which experiences did it pair 👎 poorly with?',
    description: '(select all that apply)',
    enabled: true,
    required: false,
    priority: 3,
  },
  reviewPotency: {
    title: 'How potent was it?',
    enabled: true,
    required: false,
    priority: 4,
  },
  reviewComments: {
    title: 'Anything else to add?',
    enabled: true,
    required: false,
    priority: 5,
  },
  reviewClaimPoints: {
    title: 'Claim your {points} points',
  },
  reviewComplete: {
    title: 'Review complete!',
    pointsVerbiage: 'Points may take up to 24 hours to reflect.',
    description: 'Each review helps us improve our recommendations - thanks for contributing!',
  },
  chat: {
    title: 'Chat Assistant',
    headerText: 'Chat Assistant',
    placeholderText: 'Ask me anything about us or the store',
    emptyStateText: 'Ask me anything about our store.',
    sendButtonText: 'Send',
    assistantName: 'Astro',
    initialSuggestions: [
      'What do you have in stock?',
      'What are your store hours?',
      'What are your current deals?',
      'How many loyalty points do I have?',
      'What is the last thing I purchased?',
      'Where is the nearest location?',
      'What products do you recommend?',
      'What are your most popular items?',
    ],
    loadingMessages: [
      'Thinking',
      'Looking that up',
      'Checking your account',
      'Putting the finishing touches on your answer',
    ],
  },
}
